import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import { Box, Typography } from '@mui/material';
import Accordion from '../../../components/Accordion/Accordion';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Commercial Industries Solutions | General Genomics"
        description="Explore General Genomics’ data-driven solutions for commercial sectors. Learn how synthetic and de-identified data drive efficiency, innovation, and compliance in industries like pharmaceuticals and insurance."
      />
      <Hero
        helper="Industries &gt; Commercial"
        headline="AI-powered insights for commercial success"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Transform Data Into Innovation
        </h2>
        <p className="action-on-view action-fade-in">
          General Genomics empowers commercial industries with cutting-edge
          data-driven solutions. Whether you&apos;re in academic research,
          pharmaceuticals, or insurance, our synthetic and de-identified data
          enhances operational efficiency, drives innovation, and ensures
          compliance. Explore how our scalable, secure solutions are tailored to
          address your industry&apos;s unique challenges. Contact us to see
          firsthand how we can transform your operations with actionable
          insights and future-ready technology.
        </p>
        <Image
          path="/images/library/lab-technicians-group-laptop-6.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/lab-technicians-man-woman-laptop.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Why Choose Us</p>
        <h2 className="action-on-view action-fade-in">
          Data Solutions for Your Industry
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Streamlined Efficiency:</strong> Leverage synthetic and
            de-identified data to optimize operations, reduce costs, and improve
            decision-making.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Innovative Insights:</strong> Drive breakthroughs in
            research and product development with tailored, actionable data
            insights.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Compliance Guaranteed:</strong> Ensure your operations meet
            the highest standards of security and regulatory compliance.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Scalable Solutions:</strong> Adapt seamlessly to evolving
            industry needs with flexible, future-ready data tools.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More of General Genomics&apos; Innovation
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into our cutting-edge AI platforms, data solutions, and
          service offerings that drive the future of genomics.
        </p>
        <div className="card-group">
          <Card
            icon="devices"
            headline="Curo46® AI Platform"
            subhead="Discover how our flagship AI-powered platform transforms genomics analysis and decision-making."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="data_usage"
            headline="Synthetic Data (SYN46™)"
            subhead="Explore the creation and use of privacy-focused synthetic data (SYN46™) to enhance research and analytics."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="build"
            headline="Our Capabilities"
            subhead="Connect with our wide range of innovative services and solutions tailored to your genomics needs."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="storage"
            headline="Data Portal"
            subhead="Access cutting-edge genomics data and solutions that drive practical applications for your projects."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/office-group-3.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; data solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Explore how our data-driven solutions support academic research,
          pharmaceuticals, insurance, and more with a focus on compliance and
          security.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What industries does General Genomics serve?"
          >
            General Genomics provides data-driven solutions for academic
            research,{' '}
            <Link to="/industries/commercial/pharmaceuticals">
              pharmaceuticals,
            </Link>{' '}
            <Link to="/industries/commercial/insurance-and-payers">
              insurance, and payers,
            </Link>{' '}
            helping each sector enhance operational efficiency,{' '}
            <Link to="/innovation">innovation,</Link> and compliance.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How do synthetic and de-identified data improve my business?"
          >
            Synthetic and de-identified data enable secure, scalable solutions
            that protect privacy while driving innovation and improving
            decision-making across various commercial industries.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics help with regulatory compliance?"
          >
            Our solutions prioritize data security and compliance with industry
            regulations, ensuring that your operations meet the highest
            standards for data handling and protection.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can I see a demo of how General Genomics' solutions work?"
          >
            Yes, we offer personalized demos that showcase how our data
            solutions can be tailored to address the unique challenges of your
            industry.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What makes General Genomics' data solutions secure and scalable?"
          >
            We focus on providing highly secure data environments that comply
            with regulatory standards, while our solutions are designed to scale
            with your business needs.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
